import { Component, OnInit, ViewEncapsulation, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, Validators } from '@angular/forms';
import { MatOption } from '@angular/material/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { map, startWith, take, filter, takeUntil } from 'rxjs/operators';
import { combineLatest, Observable, of, ReplaySubject } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import _ from 'lodash';
import { StateFormComponent } from './state-form/state-form.component';
import { ConfirmDialogComponent } from 'app/shared/confirm-dialog/confirm-dialog.component';
import { ITimeAggregation } from 'app/shared/interfaces/component';
import { ComponentsService } from 'app/components/services/components.service';
import { CommonService, Measurement } from '../../services/common.service';
import { RouteNavLockService } from 'app/services/route-nav-lock.service';
import { ICalTagDetail, ITagsPropsName, TagDetail } from '../../../services/graphqlApi.service';
import { DynamicDrawerService } from '../../dynamic-drawer.service';
import { DataService } from 'app/shared/data/data.service';
import { TagsService } from './../../services/tags.service';
import { IModelComponents, ISelectedTag, TagType } from './../interfaces/tag-type';
import { ITagInputs, State, Tag, ICalculatedTagDetail } from '../interfaces/tag';
import { ECalTagsItemsTypes, EEntityTypes, EReturnTypes, ETagPropertyDataType } from 'app/shared/enums';
import { Property } from 'app/shared/create-properties/interfaces/interfaces';
import { IDropdown } from 'app/shared/interfaces/model';
import { ITranslate } from 'app/shared/constants';

@Component({
  selector: 'app-create-tag',
  templateUrl: './create-tag.component.html',
  styleUrls: ['./create-tag.component.scss'],
  encapsulation: ViewEncapsulation.None
})

export class CreateTagComponent implements OnInit {
  @ViewChild(StateFormComponent) stateComponent: StateFormComponent;
  readonly destroyed = new ReplaySubject(1);
  formGroupTags = this.formBuilder.group({
    name: ['', [Validators.required]],
    description: [''],
    displayName: ['', [Validators.required]],
    tagTypeId: ['', [Validators.required]],
    precisionSample: ['136986497'],
    historizedId: ['', [Validators.required]],
    allowWrite: [false]
  });

  decimalValue: any;
  allTags: Tag[] | TagDetail[] = [];
  allModelTags: Tag[] | TagDetail[] = [];
  calculatedTagInfo: ICalculatedTagDetail;
  disableCalculatedType: boolean = false;
  isCalRetrunTypeValid: boolean = false; // for cal return type validation
  calculatedExecuted: boolean = false;
  isCalculatedTag: boolean = false;
  showAllowWrites: boolean = false;
  isModelCreate: boolean = false;
  isDisplayNameChanged: boolean = false;
  ngModelChecked: boolean = false;
  submitted: boolean = false;
  viewCalTagDetail: ICalTagDetail = null;
  cursorPosition: number = 0;
  tagsDetail: ISelectedTag[] = [];
  tagTypes: TagType[] = [];
  modelTagTypes: TagType[] = [];
  displayEUData: any[] = [];
  stateList: State[] = [];
  allProperties: Property[] = [];
  allAggregations: ITimeAggregation[] = [];
  tagDataTypes = ETagPropertyDataType;
  selectedTagType: { name: string, id: string };
  historizeTypes: Array<{ id: string, name: string }> = [];
  returnTypes: IDropdown[] = [{ name: 'Numeric', value: 'Numeric' }, { name: 'String', value: 'String' }];
  calReturnTypes = EReturnTypes;
  selectedHistorizeType: String = '';
  previousTagName: string = '';
  tagTitle: string = '';
  filteredOptionsdisplayEU: Observable<{ name: string, catalogSymbol: string, id: string }[]> = of([]);
  displayOptionEu: Observable<{ name: string, catalogSymbol: string, id: string }[]>;
  measurementCategories: Observable<Measurement[]> = of([]);
  selectedMeasurement: Measurement = null;
  data: TagDetail;
  dynamicControls: any;
  selectedDispleayEu: any;
  tagsNames: ITagsPropsName[] = []; // distinct union of tag names across all entities
  filteredTagNames: Observable<ITagsPropsName[]> = of([]);
  trans: ITranslate = { text: '' }; // for translation

  constructor(private readonly router: Router, private readonly route: ActivatedRoute, private formBuilder: FormBuilder,
    private readonly tagService: TagsService,
    private readonly dialog: MatDialog,
    private readonly toastr: ToastrService,
    private readonly componentsService: ComponentsService,
    private readonly dynamicDrawerService: DynamicDrawerService<Tag>,
    private readonly routeNavLockService: RouteNavLockService,
    private readonly dataService: DataService,
    private readonly translate: TranslateService,
    private readonly commonService: CommonService) {
    this.setHistorized();
    this.getTagsNames();
    this.dynamicDrawerService.createTagFromModel$.pipe(takeUntil(this.destroyed)).subscribe((createTagFromModel: boolean) => {
      this.isModelCreate = createTagFromModel;
    });

    // get properties
    this.dataService.properties$.pipe(takeUntil(this.destroyed)).subscribe((properties: Property[]) => {
      this.allProperties = [...properties];
      console.log('this.allProperties: ', this.allProperties);
    });

    // get agggregation
    this.componentsService.aggregations$.pipe(takeUntil(this.destroyed)).subscribe((aggregations: ITimeAggregation[]) => {
      this.allAggregations = [...aggregations];
      console.log('this.allAggregations: ', this.allAggregations);
    });

    this.tagService.tagTypes$.pipe(takeUntil(this.destroyed)).subscribe(res => {
      this.tagTypes = res;

      if (this.isModelCreate && this.isModelPage) {
        this.removeExtraTagTypes();
      }
    });

    this.dataService.tagDetails$.pipe(takeUntil(this.destroyed)).subscribe(tag => {
      if (tag) {
        this.data = tag;
        console.log('this.data: ', this.data);
        this.addDynamicControls(tag.tagType);
        if (tag.tagType === ETagPropertyDataType.Number) {
          this.setMeasurmentCategories();
          this.showAllowWrites = true;
          this.isCalculatedTag = false;
        }

        if (this.data.name !== this.data.displayName) {
          this.isDisplayNameChanged = true;
        }

        this.setFormValues();
        // view data of cal tag detail
        if (tag?.calc_tag_config) {
          this.formGroupTags.get('returnType').setValue(tag.calc_tag_config.output_type);
          this.isCalculatedTag = true;
          this.viewCalTagDetail = tag.calc_tag_config;
          // append component instance name (in case of model calTag)
          if (this.viewCalTagDetail.script_input_tags[0]?.componentInstanceId) {
            const modelComponents = this.dataService.modelComponents.getValue();
            this.viewCalTagDetail.script_input_tags.forEach((tag: ITagInputs) => {
              modelComponents?.forEach((modelComponent: IModelComponents) => {
                if (!tag.name?.includes(".") && tag.componentInstanceId === modelComponent.componentInstanceId) {
                  tag.name = modelComponent.componentInstanceName + "." + tag.name;
                }
              });
            });
          }
        }

        this.selectedTagType = { id: tag.tagTypeId, name: tag.tagType };
        // disable form
        if (this.isAssetPage || (this.isModelPage && this.data?.disableDeleteAction)) {
          this.formGroupTags.disable();
        }
      }

      // in save & add new case, updating the tagsNames list
      if(this.previousTagName){
        this.tagsNames.push({ name: this.previousTagName });
        this.autoCompleteTagName();
      }
    });

    this.formGroupTags.controls['precisionSample'].disable();

    // get all tags
    this.tagService.entityTags$.pipe(takeUntil(this.destroyed)).subscribe(tags => {
      if (tags?.length) {
        this.allTags = _.sortBy([...tags], ["name"]);
      }
    });

    // show only tag type "calculated" while create/update tag from model
    if (this.isModelPage && this.isModelCreate) {
      const isCalculatedAllowed: boolean = this.dataService._isCalculatedAllowed.getValue();
      // console.log('isCalculatedAllowed: ', isCalculatedAllowed);
      // if(!isCalculatedAllowed){
      // this.disableCalculatedType = true; // to disable calculated option if only one comp/CI
      // }

      // add new property newName to append compInstanceName with tag
      this.dataService.tags$.pipe(takeUntil(this.destroyed)).subscribe(tags => {
        this.allModelTags = [];
        const modelTags = [...tags];
        if (modelTags?.length) {
          this.allModelTags = _.sortBy([...modelTags], ["name"]);
          this.allModelTags?.forEach(modelTag => {
            modelTag.dummyName = modelTag.componentInstanceName ? modelTag.componentInstanceName + "." + modelTag.name : modelTag.name;
          });
        }
      });
    }
  }

  ngOnInit(): void {
    if (this.router.url.includes('/components')) {
      this.tagTitle = 'Component';
    } else if (this.router.url.includes('/assets')) {
      this.tagTitle = 'Asset';
    } else {
      this.tagTitle = 'Model';
    }

    this.dynamicDrawerService.input$.subscribe(res => {
      if (!this.formGroupTags.get('name').value) {
        return;
      }
      if (res.action === 'create-tag') {
        if (res.hasOwnProperty('tagValid')) {
          if (res['tagValid']) {
            if (res['createSingle']) {
              this.dynamicDrawerService.close();
            }
            this.clearForm();
          } else {
            this.toastr.error(this.commonService.translateWord('Component.sameTagNameError'));
          }
        }

      }
    });

    this.tagService.getTagTypes();
    this.tagService.setHistorized();
    this.commonService.setMasurements();
        // translation
        this.translate.onLangChange.subscribe(langChange => this.getTranslation());
        this.getTranslation();
  }

    /**
   * get translation
   */
    getTranslation(){
      this.translate.get('Component').subscribe(components => this.trans = components );
    }

  /**
   * remove all other tag tyes other than "Calculated" while creating new tag from model
   */
  removeExtraTagTypes() {
    const tagType = this.tagTypes?.find(tag => tag.name === "Calculated");
    if (tagType) {
      this.tagTypes = [];
      this.tagTypes.push(tagType);
    }
  }

  /**
   * add state
   * @param state state
   */
  addState(state: State) {
    this.stateList.push(state);
  }

  /**
   * remove state
   * @param index index
   */
  removeState(index: number) {
    this.stateList.splice(index, 1);
  }

  /**
   * filter display uoms
   * @param value value
   * @param uoms uoms
   */
  private filterDisplayEU(value, uoms) {
    const lowercaseValue = value.toLowerCase();
    return uoms.filter(optionEu =>
      optionEu.name.toLowerCase().includes(lowercaseValue)
    );
  }

  /**
   * on display EU selection
   * @param option selected eu option
   */
  onDisplayEuSelection(option: MatOption) {
    const name = option.value.split('(')[0];
    this.filteredOptionsdisplayEU.pipe(take(1)).subscribe(displayEuList => {
      this.selectedDispleayEu = displayEuList.find(d => d.name === name);
    });
  }

  /**
   * set display EU
   * @param displayEuId display EU id
   */
  setDisplayEu(displayEuId: string) {
    this.filteredOptionsdisplayEU
      .pipe(take(1))
      .subscribe(displayEuList => {
        this.selectedDispleayEu = displayEuList.find(d => d.id === displayEuId);
        if (!this.getControl('displayEuId')?.value && this.selectedDispleayEu) {
          this.getControl('displayEuId')?.setValue(this.selectedDispleayEu?.name || null);
        }
      });
  }

  /**
   * set measurement
   * @param id id
   */
  setMeasurement(id: number) {
    this.measurementCategories.pipe(filter(m => m.length > 0), take(1)).subscribe(measurements => {
      const measurement = measurements.find(m => m.id === id);
      if (measurement) {
        this.onMeasurementSelection(measurement.name);
        // this.setDisplayEu(this.data?.displayEuId || null);
        this.formGroupTags.controls['measurementCategory']?.setValue(measurement.name);
      }else{
        this.onMeasurementInput(null);
      }
    });
  }

  /**
   * on measurement selected
   * @param option measurement option
   */
  onMeasurementSelection(option: string) {
    this.getControl('displayEuId')?.setValue('');
    this.displayEUData = [];
    const measurement$ = this.measurementCategories.pipe(
      map(measurments => measurments.find(m => m.name === option)));
    var uoms$ = of([]);
    measurement$.pipe(take(1)).subscribe(m => {
      this.selectedMeasurement = m || null;
      this.getControl('displayEuId')?.setValue(m?.baseUom);
      this.selectedDispleayEu = { id: m.baseUomId, name: m.baseUom };

      if (m?.id) {
        this.commonService.getUomByMeasurmentId(m.id).pipe(take(1)).subscribe(uom => {
          uoms$ = of(uom);
          this.filteredOptionsdisplayEU = combineLatest([
            uoms$, this.formGroupTags.get('displayEuId')?.valueChanges.pipe(startWith(''))
          ]).pipe(map(([uoms, value]) => value ? this.filterDisplayEU(value, uoms) : uoms.slice()));

          this.setDisplayEu(this.data?.displayEuId || null);
        });
      }
    });

  }

  /**
   * set historized value
   */
  setHistorized() {
    this.tagService.historizedTypes$.pipe(takeUntil(this.destroyed)).subscribe(data => {
      this.historizeTypes = data;
      const defaultHistorizeType = this.historizeTypes?.find(type => type.name === "Always");
      if (defaultHistorizeType) {
        this.formGroupTags.get("historizedId").setValue(defaultHistorizeType.id);
      }
    });
  }

  get isComponentPage() {
    return this.router.url.includes(EEntityTypes.COMPONENTS);
  }

  get isModelPage() {
    return this.router.url.includes(EEntityTypes.MODELS);
  }

  get isAssetPage() {
    return this.router.url.includes(EEntityTypes.ASSETS);
  }

  // convenience getter for easy access to form fields
  get f() {
    return this.formGroupTags.controls;
  }

  get isThousandSeperatorChecked() {
    return this.formGroupTags.get('isThousandSeparator').value;
  }

  get getPercisionValue() {
    return this.formGroupTags.get('precisionSample').value;
  }

  get getDecimalValue() {
    return this.formGroupTags.get('precision').value;
  }

  /**
   * format number field
   * @param checked checked status
   */
  formatNumberField(checked: boolean) {
    if (checked) {
      return this.formGroupTags.controls['precisionSample'].setValue(this.getPercisionValue?.replace(/\B(?=(\d{3})+(?!\d))/g, ','));
    } else {
      return this.formGroupTags.controls['precisionSample']?.setValue(this.getPercisionValue?.replace(/,/g, ''));
    }
  }

  /**
   * on decimal change
   */
  onDecimalChange() {
    const value = this.getPercisionValue?.replace(/,/g, '');
    this.formGroupTags.controls['precisionSample'].patchValue(parseFloat(value).toFixed(this.getDecimalValue));
    this.formatNumberField(this.isThousandSeperatorChecked);
  }

  /**
   * set measurement categories
   */
  setMeasurmentCategories() {
    this.measurementCategories = combineLatest([
      this.commonService.measurements$,
      this.formGroupTags.get('measurementCategory')?.valueChanges.pipe(startWith(''))
    ]).pipe(map(([measurments, value]) => {
      const lowercaseValue = value?.toLowerCase() || null;
      return value ? measurments.filter(m => m.name.toLowerCase().includes(lowercaseValue)) : [...measurments];
    }));
  }

  onMeasurementInput(v: string){
    if(!v){
      this.formGroupTags.get('measurementCategory')?.setValue(null);
      this.formGroupTags.get('displayEuId')?.setValue(null);
      this.selectedDispleayEu = null;
      this.selectedMeasurement = null;
    }
  }

  /**
   * set values In form
   */
  setFormValues() {
    if (this.data) {
      this.formGroupTags.controls['name'].setValue(this.data.name);
      this.formGroupTags.controls['description'].setValue(this.data.description);
      this.formGroupTags.controls['displayName'].setValue(this.data.displayName);
      this.formGroupTags.controls['tagTypeId'].setValue(this.data.tagTypeId);
      if (this.data.historizedId) {
        this.tagService.historizedTypes$.pipe(takeUntil(this.destroyed)).subscribe(data => {
          this.historizeTypes = data;
          this.formGroupTags.patchValue({ historizedId: this.data.historizedId });
        });
      }

      this.formGroupTags.controls['allowWrite'].setValue(this.data.allowWrite);
      if (this.data.tagType === ETagPropertyDataType.Number || this.data.tagType === 'Calculateddd') { //// // return type will be handled from here
        this.setMeasurement(this.data.measurementCategory);
        this.formGroupTags.controls['isThousandSeparator'].setValue(this.data.isThousandSeparator);
        this.formGroupTags.controls['rejectMin'].setValue(this.data.rejectMin);
        this.formGroupTags.controls['rejectMax'].setValue(this.data.rejectMax);
        this.formGroupTags.controls['precision'].setValue(this.data.precision);
        if (this.data.tagType === ETagPropertyDataType.Number) {
          this.formGroupTags.controls['histPercentChange'].setValue(this.data.histPercentChange || null);
        }

        this.formatNumberField(this.data.isThousandSeparator);
      }

      if (this.data.tagType === ETagPropertyDataType.State) {
        this.stateList = this.data?.states ? JSON.parse(JSON.stringify(this.data.states)) : [];
      }

      if (this.data.tagType === ETagPropertyDataType.Calculated) {
        this.formGroupTags.controls['calculate']?.setValue(null); //////
        // this.formGroupTags.controls['interval'].setValue(null); //////
      }
    }
  }

  /**
   * to return on change status
   * @param id historize type id
   */
  isOnChange(id: string) {
    const historized = this.historizeTypes.find(h => h.id === id);
    return historized ? historized.name === 'On Change' : false;
  }

  /**
   * isEdited check
   */
  get isEdited() {
    // console.log('this.data:34 ', this.data);
    // console.log('this.formGroupTags.value :>> ', this.formGroupTags.value);
    if (!this.data || Object.keys(this.data).length === 0) return true;
    let edited = false;
    for (const key of Object.keys(this.data)) {
      if (key === 'tagTypeId' || key === 'tagType') {
        edited = this.data['tagTypeId'] !== this.formGroupTags.controls['tagTypeId'].value;
        // console.log('edited:1 ', edited);
      } else if (this.formGroupTags.controls.hasOwnProperty(key) && key === 'displayEuId') {
        edited = this.data['displayEuId'] != this.selectedDispleayEu?.id;
        // console.log('edited:2 ', edited);
      }
      else if (this.formGroupTags.controls.hasOwnProperty(key) && key === 'measurementCategory') {
        edited = this.data['measurementCategory'] != this.selectedMeasurement?.id;
        // console.log('edited:3 ', edited);
      } else if (this.selectedTagType.name !== ETagPropertyDataType.State && this.formGroupTags.controls.hasOwnProperty(key) && key !== 'measurementCategory' && key !== 'displayEuId' && this.data[key] !== this.formGroupTags.controls[key].value) {
        edited = true;
        // console.log('edited:4 ', edited);
      } else if (JSON.stringify(this.data.states) !== JSON.stringify(this.stateList)) {
        edited = true;
        // console.log('edited:5 ', edited);
      }

      if (edited) {
        break;
      }
    }

    if (this.data.id && this.selectedTagType.name === ETagPropertyDataType.State && (this.data.states.length !== this.stateList.length || this.formGroupTags.controls['name'].value !== this.data.name || this.formGroupTags.controls['displayName'].value !== this.data.displayName || this.formGroupTags.controls['historizedId'].value !== this.data.historizedId || this.formGroupTags.controls['description'].value !== this.data.description)) {
      return true;
    }

    // // check for change in cal expression
    // if (this.selectedTagType.name === 'Calculated' && this.viewCalTagDetail?.script !== this.calculatedTagInfo?.script && this.calculatedTagInfo?.outputType) {
    if (this.selectedTagType.name === 'Calculated' && this.calculatedTagInfo?.outputType) {
      return true;
    }

    return edited;
  }

  /**
   * get control
   * @param field field name
   */
  getControl(field): FormControl {
    return this.formGroupTags.get(field) as FormControl;
  }

  /**
   * add dynamic field control
   * @param tagType tag type
   */
  addDynamicControls(tagType: string) {
    switch (tagType) {

      case ETagPropertyDataType.Number:
        this.dynamicControls = {
          histPercentChange: [null],
          rejectMin: [0, [Validators.required]],
          rejectMax: [10000, [Validators.required]],
          isThousandSeparator: [''],
          precision: ['2', [Validators.required]],
          measurementCategory: [null],
          displayEuId: [null],
          precisionSample: ['136986497']
          // 'offset': [0],
          // 'factor': [1]
        };
        break;

      case ETagPropertyDataType.State:
        this.dynamicControls = {};
        break;

      case ETagPropertyDataType.Text:
        this.dynamicControls = {};
        break;

      case ETagPropertyDataType.Calculated:
        this.dynamicControls = {
          'calculated': ['', [Validators.required]],
          // 'interval': [null, [Validators.required]],
          'availableTag': [''],
          'availableProp': [''],
          'availableAggr': [''],
          'availableComponent': [''],
          // 'rejectMin': [0, [Validators.required]],
          // 'rejectMax': [10000, [Validators.required]],
          // 'isThousandSeparator': [''],
          // 'precision': ['2', [Validators.required]],
          'returnType': ['', [Validators.required]],
        };
        break;

      default:
        break;
    }

    this.addValidationControls();
  }

  /**
   * on tag selection change
   */
  onTagTypeChanged() {
    const id = this.formGroupTags.controls['tagTypeId'].value;
    const selectedTagType = this.tagTypes.find(el => el.id === id);
    this.isCalculatedTag = (selectedTagType.name === "Calculated") ? true : false;
    this.removeDynamicControl();
    this.addDynamicControls(selectedTagType?.name)
    this.setHistorizedType(selectedTagType?.name);
    this.addValidationControls();
    this.selectedTagType = selectedTagType;
    if (this.selectedTagType?.name === ETagPropertyDataType.Number) {
      this.formGroupTags.get('precisionSample').setValue('136986497');
      this.formGroupTags.controls['precisionSample'].disable();
      this.formGroupTags.controls['precisionSample'].patchValue(parseFloat(this.getPercisionValue?.replace(/,/g, '')).toFixed(this.getDecimalValue));
      this.formatNumberField(false);
      this.setMeasurmentCategories();
      this.showAllowWrites = true;
    } else {
      this.showAllowWrites = false;
    }
  }

  /**
   * set historized type
   * @param name name
   */
  setHistorizedType(name: string) {
    this.tagService.historizedTypes$.pipe(take(1)).subscribe(types => {
      if (name === ETagPropertyDataType.State || name === ETagPropertyDataType.Text) {
        const alwaysHistorized = types.find(h => h.name === 'On Change');
        this.getControl('historizedId').setValue(alwaysHistorized?.id);
      } else {
        const alwaysHistorized = types.find(h => h.name === 'Always');
        this.getControl('historizedId').setValue(alwaysHistorized?.id);
      }
    });
  }

  /**
   * remove dynamic control
   */
  removeDynamicControl() {
    if (this.dynamicControls) {
      Object.entries(this.dynamicControls).forEach((el, i) => {
        this.formGroupTags.removeControl(el[0]);
      });
    }
  }

  /**
   * add validation controls
   */
  addValidationControls() {
    if (this.dynamicControls) {
      for (let [prop, value] of Object.entries(this.dynamicControls)) {
        this.formGroupTags.addControl(prop,
          this.formBuilder.control(value[0], Validators.compose(value[1])));
      }
    }
  }

  /**
   * on historize change
   * @param value value
   */
  onHistorizeChanged(value) {
    this.selectedHistorizeType = value;
  }

  /**
   * close tag component
   */
  close() {
    let isStateChanged: boolean = false;
    if (this.isEdited && this.selectedTagType?.name === ETagPropertyDataType.State) {
      isStateChanged = true;
      const dialogRef = this.dialog.open(ConfirmDialogComponent, { data: 'There are some unsaved changes.' });
      dialogRef.afterClosed().subscribe((response: boolean) => {
        console.log('response: ', response);
        if (response) {
          this.dynamicDrawerService.close(true);
        }
      });
    };

    if (!isStateChanged) {
      this.dynamicDrawerService.close(true);
      this.clearForm();
    }
  }

  /**
   * save/update, add tag
   * @param createSingle is create Single tag at a time and close
   */
  save(createSingle = false) {
    const details = {};
    Object.keys(this.formGroupTags.value).forEach((key) => {
      if (this.dynamicControls?.hasOwnProperty(key)) {
        details[key] = this.formGroupTags.value[key];
      }
    });

    const tagDetail = {} as Tag;
    if (this.data) {
      tagDetail.id = this.data.id;
    }

    this.previousTagName = this.formGroupTags.value['name'];
    tagDetail.name = this.formGroupTags.value['name'];
    tagDetail.description = this.formGroupTags.value['description'];
    tagDetail.displayName = this.formGroupTags.value['displayName'];
    tagDetail.tagTypeId = this.formGroupTags.value['tagTypeId'];
    tagDetail.historizedId = this.formGroupTags.value['historizedId'];
    tagDetail.allowWrite = this.formGroupTags.value['allowWrite'] || false;
    tagDetail.tagType = this.selectedTagType.name;
    tagDetail.details = {};

    if (this.selectedTagType.name === ETagPropertyDataType.Text) {
      tagDetail.details = { value: '' };
    } else if (this.selectedTagType.name === ETagPropertyDataType.Number) {
      // tagDetail.details.factor = parseInt(details['factor']);
      // tagDetail.details.offset = parseInt(details['offset']);
      tagDetail.details.measurementCategory = this.selectedMeasurement?.id;
      tagDetail.details.isOverride = false;
      tagDetail.details.precision = parseInt(details['precision']);
      tagDetail.details.rejectMax = parseInt(details['rejectMax']);
      tagDetail.details.rejectMin = parseInt(details['rejectMin']);
      tagDetail.details.isThousandSeparator = details['isThousandSeparator'] || false;
      tagDetail.details.displayEuId = this.selectedDispleayEu?.id || null;
      if (this.isOnChange(this.formGroupTags.value['historizedId'])) {
        tagDetail.details.histPercentChange = parseInt(details['histPercentChange']) || null;
      } else {
        tagDetail.details.histPercentChange = null;
      }

      this.showAllowWrites = false;
    } else if (this.selectedTagType.name === ETagPropertyDataType.State) {
      tagDetail.details = {
        ...tagDetail.details,
        states: this.stateList
      };
    } else if (this.selectedTagType.name === ETagPropertyDataType.Calculated) {
      tagDetail.details = this.calculatedTagInfo;
      this.isCalculatedTag = false;
    }

    console.log('tagDetail output:>> ', tagDetail);
    this.dynamicDrawerService.passOuptut(tagDetail, createSingle);
    this.isDisplayNameChanged = false;
    this.clearForm();
    this.dynamicControls = {};
    // set always historize
    this.setHistorized();
  }

  /**
   * calculated tag detail
   * @param calculatedTagDetail cal tag detail
   */
  calculatedTagDetail(calculatedTagDetail: ICalculatedTagDetail) {
    this.calculatedExecuted = (calculatedTagDetail?.outputType) ? true : false;
    console.log('this.calculatedExecuted: ', this.calculatedExecuted);
    this.calculatedTagInfo = calculatedTagDetail;
    console.log('this.calculatedTagInfo: ', JSON.parse(JSON.stringify(this.calculatedTagInfo)));
    if (this.calculatedExecuted) {
      this.isCalRetrunTypeValid = (calculatedTagDetail.outputType === this.formGroupTags.get('returnType').value) ? true : false;
    }

    console.log('this.isCalRetrunTypeValid: ', this.isCalRetrunTypeValid);
    const inputTags: ITagInputs[] = [];
    const inputProps: ITagInputs[] = [];
    const inputAggrs: ITagInputs[] = [];
    if (this.calculatedTagInfo && this.isComponentPage) {
      this.calculatedTagInfo.scriptInputTags?.forEach((calInputTag: ITagInputs) => {
        this.allTags.forEach(tag => {
          if (calInputTag.name === tag.name && calInputTag.itemType === ECalTagsItemsTypes.Tag) {
            inputTags.push({ id: tag.id, name: tag.name });
          }
        });
      });

      console.log('this.allProperties: ', this.allProperties);
      this.calculatedTagInfo.scriptInputProps?.forEach((calInputProp: ITagInputs) => {
        this.allProperties.filter(prop => {
          if (calInputProp.name === prop.name && calInputProp.itemType === ECalTagsItemsTypes.Property) {
            inputProps.push({ id: prop.id, name: prop.name });
          }
        });
      });

      console.log('this.allAggregations: ', this.allAggregations);
      this.calculatedTagInfo.aggConfigs?.forEach((calInputProp: ITagInputs) => {
        this.allAggregations.filter((aggr: ITimeAggregation) => {
          if (calInputProp.name === aggr.name && calInputProp.itemType === ECalTagsItemsTypes.Aggregation) {
            inputAggrs.push({ id: aggr.id, name: aggr.name });
          }
        });
      });

      console.log('inputTags: ', JSON.parse(JSON.stringify(inputTags)));
      this.calculatedTagInfo.scriptInputTags = inputTags;
      this.calculatedTagInfo.scriptInputProps = inputProps;
      this.calculatedTagInfo.aggConfigs = inputAggrs;
    } else if (this.calculatedTagInfo && this.isModelPage) {
      let modelId: string = '';
      this.route.queryParams.subscribe((params) => { modelId = params?.id });

      this.calculatedTagInfo.scriptInputTags?.forEach((calInputTag: ITagInputs) => {
        const [componentInstanceName, name] = calInputTag.name.split(".");
        this.allModelTags.forEach((tag) => {
          if ((calInputTag.name === tag.dummyName) && (!tag.componentInstanceId || componentInstanceName === tag.componentInstanceName)) {
            inputTags.push({ id: tag.id, name: `${tag.name}`, modelId: modelId, componentInstanceId: tag.componentInstanceId || null, componentInstanceName: tag.componentInstanceName || null });
          }
        });
      });

      console.log('this.allProperties: ', this.allProperties);
      this.calculatedTagInfo.scriptInputProps?.forEach((calInputProp: ITagInputs) => {
        const [componentInstanceName, name] = calInputProp.name.split(".");
        this.allProperties.forEach((prop: Property) => {
          if ((calInputProp.name === prop.dummyName) && (!prop.componentInstanceId || componentInstanceName === prop.componentInstanceName)) {
            inputProps.push({ id: prop.id, name: `${prop.name}`, modelId: modelId, componentInstanceId: prop.componentInstanceId || null, componentInstanceName: prop.componentInstanceName || null });
          }
        });
      });

      console.log('this.allAggregations: ', this.allAggregations);
      this.calculatedTagInfo.aggConfigs?.forEach((aggConfig: ITagInputs) => {
        const [compInstanceName, name] = aggConfig.name.split(".");
        this.allAggregations.forEach((aggregation: ITimeAggregation) => {
          if ((aggConfig.name === aggregation.dummyName) && (!aggregation.compInstanceId || compInstanceName === aggregation.compInstanceName)) {
            inputAggrs.push({ id: aggregation.id, name: `${aggregation.name}`, modelId: modelId, componentInstanceId: aggregation.compInstanceId || null, componentInstanceName: aggregation.compInstanceName || null });
          }
        });
      });

      this.calculatedTagInfo.scriptInputTags = inputTags;
      this.calculatedTagInfo.scriptInputProps = inputProps;
      this.calculatedTagInfo.aggConfigs = inputAggrs;
      console.log('this.calculatedTagInfo: ', JSON.parse(JSON.stringify(this.calculatedTagInfo)));
    }
  }

  /**
   * clear form
   */
  private clearForm() {
    this.dataService.setTagsEditMode(false);
    this.dataService.setTagDetails({} as TagDetail);
    this.formGroupTags.reset();
    this.removeDynamicControl();
    this.stateList = [];
  }

  /**
   * allow write change
   * @param event event
   */
  onAllowWriteChanged(event) {
    this.formGroupTags.controls['allowWrite'].setValue(event.checked);
  }

  /**
   * lock route
   */
  lockRouting() {
    this.routeNavLockService.lockRoute({ url: this.route.snapshot.url[0].path, isLocked: true });
  }

  /**
   * unlock route
   */
  unlockRoute() {
    this.routeNavLockService.unlockRoute({ url: this.route.snapshot.url[0].path, isLocked: false });
  }

  /**
   * on return type changed for cal tags expression
   * @param selectedReturnType selected return type for cal expression
   */
  onReturnTypeChanged(selectedReturnType: string) {
    if (selectedReturnType === EReturnTypes.String && this.dynamicControls) {
      this.formGroupTags.removeControl('rejectMin');
      this.formGroupTags.removeControl('rejectMax');
      this.formGroupTags.removeControl('isThousandSeparator');
      this.formGroupTags.removeControl('precision');
    } else {
      // this.formGroupTags.addControl('rejectMin', this.formBuilder.control(0, Validators.required));
      // this.formGroupTags.addControl('rejectMax', this.formBuilder.control(10000, Validators.required));
      // this.formGroupTags.addControl('precision', this.formBuilder.control('2', Validators.required));
      // this.formGroupTags.addControl('isThousandSeparator', this.formBuilder.control(''));
    }

    this.formatNumberField(false);
    if (this.calculatedExecuted) {
      this.isCalRetrunTypeValid = (this.calculatedTagInfo?.outputType === this.formGroupTags.get('returnType').value) ? true : false;
    }
  }

  /**
   * to show validation messages on tooltip
   */
  getValidationsTooltip(isSaveAndAdd: boolean = false) {
    if (this.isCalculatedTag) {
      let errorMessage = '';
      if (this.formGroupTags.invalid) {
        errorMessage = this.commonService.translateWord('Component.formInvalid')
      }

      if (!this.calculatedExecuted && !this.calculatedTagInfo?.isSingleEntity) {
        errorMessage = errorMessage + this.commonService.translateWord('Component.expressionInvalid');
      }

      if (!this.isCalRetrunTypeValid && !this.calculatedTagInfo?.isSingleEntity) {
        errorMessage = errorMessage + this.commonService.translateWord('Component.returnTypeError');;
      }

      if (this.calculatedTagInfo?.isSingleEntity && this.isModelPage) {
        errorMessage = errorMessage + this.commonService.translateWord('Component.tagSelectionError');
      }

      return errorMessage ?  errorMessage : isSaveAndAdd ? this.commonService.translateWord('Component.SaveAndAddNew') : this.commonService.translateWord('Component.AddAndClose');
    } else {
      return isSaveAndAdd ? this.commonService.translateWord('Component.SaveAndAddNew') :this.commonService.translateWord('Component.AddAndClose');
    }

  }

  //#region tag autocomplete functionality and its validation

  /**
   * get tags names
   */
  getTagsNames() {
    this.componentsService.getTagsNames().pipe(take(1)).subscribe((tagNames: ITagsPropsName[]) => {
      if (tagNames.length) {
        if (this.data) {
          tagNames = tagNames.filter((tagName: ITagsPropsName) => tagName.name !== this.data.name);
        }

        this.tagsNames = [...tagNames];
        this.autoCompleteTagName();
      }
    });
  }

  /**
 * auto complete on tag name
 */
  autoCompleteTagName() {
    const tags$ = of(this.tagsNames);
    this.filteredTagNames = combineLatest([tags$, this.formGroupTags.controls['name']?.valueChanges.pipe(startWith(this.data ? this.data.name : ''))
    ]).pipe(map(([tags, value]) => {
      const lowercaseValue: string = value?.toLowerCase() || '';
      if(lowercaseValue){
        if(this.formGroupTags.get('displayName').value === value){
          this.isDisplayNameChanged = false;
        }

        if(!this.isDisplayNameChanged){
          this.formGroupTags.get('displayName').setValue(value);
        }

        if(!this.data || (lowercaseValue !== (this.data?.name?.toLowerCase()))){
          return lowercaseValue ? tags?.filter((tag: ITagsPropsName) => tag.name.toLowerCase().includes(lowercaseValue)) : [...tags];
        }
      }
    }));
  }

  onDisplayNameInput(text: string){
    this.isDisplayNameChanged = (text === this.formGroupTags.get('name').value) ? false : true;
  }

  /**
   * input event to check if name already exists
   * @param text typed text
   */
  onNameInput(text: string) {
    const tagNames: string[] = this.dataService.tags.getValue().map((tag: TagDetail) => tag.name.toLowerCase());
    if(this.data?.name){
      const index: number = tagNames.findIndex((tagName: string)=> tagName === this.data.name.toLowerCase());
      tagNames.splice(index, 1);
    }

    if (tagNames.includes(text.toLowerCase())) {
      this.formGroupTags.controls['name'].setErrors({ 'duplicate': true });
      this.formGroupTags.controls['name'].markAsTouched();
    } else {
      this.formGroupTags.controls['name'].setErrors(null);
      if (!text) {
        this.formGroupTags.controls['name'].setErrors(Validators.required);
        this.formGroupTags.controls['name'].markAsTouched();
      } else {
        const regexExpression: RegExp = /^[a-zA-Z]([a-zA-Z0-9_/-\s_.])+$/; // enter more than 1 letters where first can't be a number
        if (regexExpression.test(text)) {
          return true;
        } else {
          this.formGroupTags.controls['name'].setErrors({ 'badExpression': true });
          this.formGroupTags.controls['name'].markAsTouched();
        }
      }
    }
  }

  //#endregion


  ngOnDestroy() {
    this.allModelTags = [];
    this.allTags = [];
    this.destroyed.next();
    this.destroyed.complete();
  }

}
